import { Link as RouterLink, useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import MenuIcon from '@mui/icons-material/Menu';
import { Grid, IconButton, Link, SvgIcon, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { checkHasPermissions, isAdmin, isPM } from '../../utils';
import { checkMenuItemAccess } from './utils';

import { ACCESS_LEVELS } from './constants';

import { adminIcon } from '../../res/svg/icons';

const ignoredProps = ['isActive'];

const StyledMenuItem = styled(Typography, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isActive }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
  '&:hover': {
    color: theme.palette.primary.main,
  },
  ...(isActive && {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  }),
}));

const LeftMenuItem = (props) => {
  const location = useLocation();
  const {
    menuItem: {
      permission,
      access,
      excludeAccess,
      type,
      url,
      label,
      icon,
      customViewBox,
      subMenu,
      subComponents,
    },
    index,
    isDrawerOpen,
    setDrawerOpen,
    isMobileScreen,
  } = props;

  const elements = [];

  // check permissions
  if (permission && !checkHasPermissions(permission)) return null;

  if (access === ACCESS_LEVELS.ADMIN && !isAdmin()) return null;
  if (access === ACCESS_LEVELS.PM && !isPM()) return null;

  if (excludeAccess?.length) {
    const hasNoAccess = excludeAccess.find(
      (accessLevel) => checkMenuItemAccess(accessLevel) === false,
    );
    if (hasNoAccess) return null;
  }

  const isActive =
    location?.pathname === url || location?.pathname.indexOf(url) === 0;
  const viewBox = customViewBox || '0 0 32 32';

  const menuIcon = (
    <Grid
      sx={[
        {
          display: 'flex',
          height: '3rem',
          fontSize: '3rem',
          margin: '1rem 1rem 1rem 0',
        },
        !isDrawerOpen && {
          margin: '0.5rem',
        },
      ]}
    >
      <SvgIcon viewBox={viewBox} fontSize="inherit">
        {icon || adminIcon}
      </SvgIcon>
    </Grid>
  );

  const closeMenuIfMobileMode = () =>
    isMobileScreen ? setDrawerOpen(!isDrawerOpen) : null;

  let navItemContent;

  if (isDrawerOpen) {
    navItemContent = (
      <Grid container alignItems="center" wrap="nowrap">
        {menuIcon}
        <StyledMenuItem isActive={isActive}>{label}</StyledMenuItem>
      </Grid>
    );
  } else {
    navItemContent = (
      <IconButton color="inherit" aria-label="Menu icon" size="large">
        {menuIcon}
      </IconButton>
    );
  }

  if (type === 'sub-header') {
    elements.push(
      <Grid
        key={`sub-header-${label}`}
        container
        justifyContent="space-between"
        alignItems="center"
        sx={
          isDrawerOpen
            ? { margin: '3rem 0 1.5rem 0' }
            : { margin: '0.5rem', width: 'auto' }
        }
      >
        {isDrawerOpen && (
          <Typography variant="h6" color="primary" sx={{ fontSize: '1.8rem' }}>
            {label}
          </Typography>
        )}
        {index === 1 && !isDrawerOpen && <div style={{ height: '2rem' }} />}
        {index === 0 && (
          <IconButton
            color="inherit"
            onClick={() => setDrawerOpen(!isDrawerOpen)}
            aria-label="Menu icon"
            size="large"
          >
            <MenuIcon style={{ fontSize: '3.2rem' }} />
          </IconButton>
        )}
      </Grid>,
    );
  }
  if (subMenu && subMenu.length) {
    elements.push(
      <LeftMenuNavComponent
        key={label}
        menuData={subMenu}
        isDrawerOpen={isDrawerOpen}
        setDrawerOpen={setDrawerOpen}
        isMobileScreen={isMobileScreen}
      />,
    );
  }
  if (type === 'item') {
    if (url && !isActive) {
      elements.push(
        <Link
          component={RouterLink}
          to={url}
          style={{ textDecoration: 'none' }}
          key={url}
          onClick={closeMenuIfMobileMode}
        >
          {navItemContent}
        </Link>,
      );
    } else {
      elements.push(<div key={label}>{navItemContent}</div>);
    }
  }
  if (subComponents?.length && isDrawerOpen) {
    subComponents.forEach((Component, index) =>
      elements.push(
        <div key={index}>
          <Component />
        </div>,
      ),
    );
  }
  return <div>{elements}</div>;
};

LeftMenuItem.propTypes = {
  menuItem: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  isDrawerOpen: PropTypes.bool.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
};

const LeftMenuNavComponent = (props) => {
  const { menuData } = props;
  return (
    <div>
      {menuData
        .filter(({ show = true }) => show)
        .map((menuItem, index) => (
          <LeftMenuItem
            key={menuItem.label}
            menuItem={menuItem}
            {...props}
            index={index}
          />
        ))}
    </div>
  );
};

LeftMenuNavComponent.propTypes = {
  menuData: PropTypes.instanceOf(Array).isRequired,
};

export default LeftMenuNavComponent;
